import { css } from "@emotion/css";
import type { PageAction } from "@octopusdeploy/design-system-components";
import { ActionButton, ActionButtonType, Button, useShowSnackbar } from "@octopusdeploy/design-system-components";
import { CodeCommitIcon } from "@octopusdeploy/design-system-icons";
import { borderWidth, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { logger } from "@octopusdeploy/logging";
import { useAggregateAPIOperationStatus, useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import { isProcessTemplateParameterResource, Permission, ValidateGitRefV2ResponseType } from "@octopusdeploy/octopus-server-client";
import type { Repository, ProjectTemplateParameterSetResource, ValidateGitRefV2Response, ActionTemplateParameterResource, ProcessTemplateParameterResource, ModifyProjectTemplateParameterSetCommand, ProjectTemplateResource, PlatformHubVersionControlSettingsResource, } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { isEqual } from "lodash";
import * as React from "react";
import { useCallback, useEffect } from "react";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import FilterSearchBox from "~/components/FilterSearchBox";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { PermissionCheck } from "~/components/PermissionCheck";
import { hasPermission } from "~/components/PermissionCheck/PermissionCheck";
import { lastAccessedPlatformHubGitRef } from "../LastAccessedPlatformHubGitRef";
import { PlatformHubPageHeaderBranchSelector } from "../PlatformHubPageHeaderBranchSelector";
import { PlatformHubVcsError } from "../PlatformHubVcsError";
import { AddTemplateParameterButton, OpenReorderParametersButton, TemplateParametersOnboarding, styles as processTemplateParametersTabStyles } from "../processTemplates/ProcessTemplateParametersTab";
import { ActionCompleted } from "../processTemplates/ProcessTemplateWizardDialog";
import { createSnackbarMessage, TemplateParametersTable } from "../processTemplates/TemplateParametersTable";
import { ProjectTemplateCommitDialog } from "./ProjectTemplateCommitDialog";
import { GetProjectTemplateInitialSuccessCallout } from "./ProjectTemplateLayout";
import ProjectTemplateOverflowMenu from "./ProjectTemplateOverflowMenu";
import ProjectTemplatePublishButton from "./ProjectTemplatePublishButton";
import { TemplateWizardSteps } from "./ProjectTemplateWizardDialog";
import { useProjectTemplateWizardDialog } from "./ProjectTemplateWizardDialogContext";
interface ProjectTemplateBranchParametersPageProps {
    loaderData: LoaderData;
    slug: string;
    gitRef: string;
}
type LoaderData = DataWithProjectTemplateParameterSet | DataWithFailedGitRefValidationResult;
type DataWithProjectTemplateParameterSet = {
    type: "success";
    projectTemplate: ProjectTemplateResource;
    parameterSet: ProjectTemplateParameterSetResource;
    persistenceSettings: PlatformHubVersionControlSettingsResource;
    gitRefValidationResult: ValidateGitRefV2Response | undefined;
};
type DataWithFailedGitRefValidationResult = {
    type: "error";
    gitRefValidationResult: ValidateGitRefV2Response;
    persistenceSettings: PlatformHubVersionControlSettingsResource;
};
export async function projectTemplateBranchParametersPageLoader(repository: Repository, { routeParams }: {
    routeParams: {
        slug: string;
        gitRef: string;
    };
}): Promise<LoaderData> {
    const persistenceSettings = await repository.PlatformHub.getVersionControlSettings();
    if (!routeParams.gitRef) {
        routeParams.gitRef = lastAccessedPlatformHubGitRef.get(persistenceSettings);
    }
    const gitRefValidationResult = await repository.PlatformHub.validateGitRef(routeParams.gitRef);
    if (gitRefValidationResult.Type !== ValidateGitRefV2ResponseType.Success) {
        return {
            type: "error",
            persistenceSettings,
            gitRefValidationResult,
        };
    }
    const projectTemplate = await repository.ProjectTemplates.get(routeParams.slug, routeParams.gitRef);
    const parameterSet = await getParameterSet(repository, routeParams.slug, routeParams.gitRef);
    return {
        type: "success",
        projectTemplate,
        parameterSet,
        persistenceSettings,
        gitRefValidationResult,
    };
}
export function ProjectTemplateBranchParametersPage({ loaderData, slug, gitRef }: ProjectTemplateBranchParametersPageProps) {
    const { doBusyTask, status: doBusyTaskStatus } = useLegacyDoBusyTask();
    const { isInProgress } = useAggregateAPIOperationStatus(doBusyTaskStatus);
    const showSnackbar = useShowSnackbar();
    const navigation = useSpaceAwareNavigation();
    const { openWizard, sharingConfiguration, showInitialSuccessCallout, setShowInitialSuccessCallout } = useProjectTemplateWizardDialog();
    const isTemplateShared = sharingConfiguration ? sharingConfiguration.SharedToAllSpaces || sharingConfiguration.IndividuallySharedSpaceIds.length > 0 : false;
    const persistenceSettings = loaderData.persistenceSettings;
    const navigateToGitRef = useCallback((newGitRef: string) => {
        const routeParams = { slug, gitRef: newGitRef };
        navigation.redirect(links.projectTemplateBranchParametersPage.generateUrl(routeParams, undefined));
        lastAccessedPlatformHubGitRef.save(newGitRef);
    }, [navigation, slug]);
    const setGitRef = navigateToGitRef;
    // Track current gitRef in a ref to detect stale queries
    const currentGitRefRef = React.useRef(gitRef);
    React.useEffect(() => {
        currentGitRefRef.current = gitRef;
    }, [gitRef]);
    const isDataWithParameters = (data: LoaderData): data is DataWithProjectTemplateParameterSet => {
        return data.type === "success";
    };
    const initialResult = isDataWithParameters(loaderData) ? loaderData.parameterSet : undefined;
    const { result: parameterSetData } = useQuery(async (repository, abortSignal) => {
        const queryGitRef = gitRef;
        const checkStale = () => currentGitRefRef.current !== queryGitRef || gitRef !== queryGitRef;
        if (abortSignal.aborted || checkStale()) {
            throw new Error("Query aborted");
        }
        const queryResult = await repository.ProjectTemplates.getParameterSet({
            ProjectTemplateSlug: slug,
            GitRef: queryGitRef,
        });
        if (checkStale() || abortSignal.aborted) {
            throw new Error("Query aborted");
        }
        return queryResult;
    }, [slug, gitRef], "Get project template parameter set", {
        initialResult,
    });
    const currentParameterSet = parameterSetData ?? (isDataWithParameters(loaderData) ? loaderData.parameterSet : undefined);
    const [model, setModel] = React.useState<ProjectTemplateParameterSetResource>(currentParameterSet);
    const [cleanModel, setCleanModel] = React.useState<ProjectTemplateParameterSetResource>(currentParameterSet);
    useEffect(() => {
        if (currentParameterSet) {
            setCleanModel(currentParameterSet);
            setModel(currentParameterSet);
        }
    }, [currentParameterSet]);
    const [nameFilter, setNameFilter] = React.useState<string>("");
    const { openDialog: openCommitPublishShareDialog, isOpen: isCommitPublishShareDialogOpen, closeAllDialogs: closeCommitPublishShareDialog } = useDialogTrigger();
    const onModelChange = (updatedModel: ProjectTemplateParameterSetResource) => {
        setModel(updatedModel);
    };
    const onParametersChange = (parameters: ActionTemplateParameterResource[]) => {
        const updatedModel: ProjectTemplateParameterSetResource = { ...model, Parameters: parameters.map((p) => (isProcessTemplateParameterResource(p) ? p : { ...p, Values: [] })) };
        onModelChange(updatedModel);
    };
    const onAddParameter = (parameter: ProcessTemplateParameterResource) => {
        onParametersChange([...model.Parameters, parameter]);
    };
    const saveProjectTemplateParametersSet = async (repository: Repository, command: ModifyProjectTemplateParameterSetCommand) => {
        await doBusyTask(async () => {
            await repository.ProjectTemplates.modifyParameterSet(command);
            const updatedCleanModel = await repository.ProjectTemplates.getParameterSet({
                ProjectTemplateSlug: slug,
                GitRef: gitRef,
            });
            setCleanModel(updatedCleanModel);
            setModel(updatedCleanModel);
        });
    };
    const { execute: executeSaveProjectTemplateParametersSet } = useMutation({
        name: "Save project template parameters set",
        action: async (repository: Repository, command: ModifyProjectTemplateParameterSetCommand) => {
            await saveProjectTemplateParametersSet(repository, command);
            return [ActionCompleted.Committed];
        },
        afterComplete: async (repository, result) => {
            const snackbarMessage = createSnackbarMessage(result, "project template");
            if (snackbarMessage) {
                showSnackbar(snackbarMessage);
            }
        },
    });
    const handleCommit = useCallback(async (command: ModifyProjectTemplateParameterSetCommand): Promise<void> => {
        await executeSaveProjectTemplateParametersSet(command);
    }, [executeSaveProjectTemplateParametersSet]);
    const createCommitHandler = useCallback(() => async (commitMessage: string) => {
        const command: ModifyProjectTemplateParameterSetCommand = {
            ProjectTemplateSlug: slug,
            GitRef: gitRef,
            ParameterSet: model,
            ChangeDescription: commitMessage,
        };
        await handleCommit(command);
    }, [slug, gitRef, model, handleCommit]);
    const getCommandToCommit = (): ModifyProjectTemplateParameterSetCommand => {
        return {
            ProjectTemplateSlug: slug,
            GitRef: gitRef,
            ParameterSet: model,
        };
    };
    const onSaveClick = async (isNavigationConfirmation: boolean) => {
        if (isNavigationConfirmation) {
            openCommitPublishShareDialog();
        }
    };
    const publishButton: PageAction = {
        type: "custom",
        content: isDataWithParameters(loaderData) ? (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <ProjectTemplatePublishButton projectTemplate={loaderData.projectTemplate}/>
            </PermissionCheck>) : null,
        key: "Publish",
    };
    const commitButton: PageAction = {
        key: "CommitButton",
        type: "custom",
        content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <div role="group" className={styles.commitButtonContainerStyles}>
                    <ActionButton label={"Commit"} type={ActionButtonType.Primary} accessibleName={"Commit"} onClick={() => {
                if (isTemplateShared) {
                    openWizard([TemplateWizardSteps.Commit, TemplateWizardSteps.Publish], undefined, createCommitHandler());
                }
                else {
                    openWizard([TemplateWizardSteps.Commit, TemplateWizardSteps.Publish, TemplateWizardSteps.Share], undefined, createCommitHandler());
                }
            }} disabled={isEqual(model, cleanModel) || isInProgress || !hasPermission(Permission.PlatformHubEdit)}/>
                    <Button importance="primary" icon={<CodeCommitIcon size={20}/>} accessibleName={"Quick commit"} disabled={isEqual(model, cleanModel) || isInProgress || !hasPermission(Permission.PlatformHubEdit)} onClick={() => handleCommit(getCommandToCommit())}/>
                </div>
            </PermissionCheck>),
    };
    const projectTemplateOverflowButton: PageAction = {
        type: "custom",
        key: "Project Template Actions",
        content: isDataWithParameters(loaderData) ? <ProjectTemplateOverflowMenu projectTemplate={loaderData.projectTemplate}/> : null,
    };
    const callout = GetProjectTemplateInitialSuccessCallout({ showInitialSuccessCallout, setShowInitialSuccessCallout, projectTemplateName: isDataWithParameters(loaderData) ? loaderData.projectTemplate.Name : "Project template" });
    if (!isDataWithParameters(loaderData)) {
        return (<PageContent header={{ title: "Parameters", contextSelector: <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} persistenceSettings={persistenceSettings}/> }}>
                <PlatformHubVcsError error={loaderData.gitRefValidationResult} gitRef={gitRef} persistenceSettings={persistenceSettings} setGitRef={setGitRef}/>
            </PageContent>);
    }
    if (!currentParameterSet || !model || model.Parameters.length === 0) {
        return (<PageContent header={{ title: "Parameters", contextSelector: <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} persistenceSettings={persistenceSettings}/> }} callout={callout}>
                <div className={processTemplateParametersTabStyles.emptyStateContainer}>
                    <TemplateParametersOnboarding onAdd={onAddParameter} parameters={model?.Parameters ?? []}/>
                </div>
            </PageContent>);
    }
    return (<>
            <LegacyForm devToolsDirtyTrackingKey="Project Template Parameters" disableDirtyFormChecking={false} onSaveClick={onSaveClick} savePermission={{ permission: Permission.PlatformHubEdit }} model={model} cleanModel={cleanModel} forceDisableFormSaveButton={isInProgress}>
                {() => (<PageContent header={{
                title: "Parameters",
                contextSelector: <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} persistenceSettings={persistenceSettings}/>,
                pageActions: [publishButton, commitButton, projectTemplateOverflowButton],
            }} busy={isInProgress} callout={callout}>
                        <div className={styles.headerContainer}>
                            <FilterSearchBox placeholder="Filter by..." value={nameFilter} onChange={setNameFilter}/>
                            <div className={styles.parameterButtonsContainer}>
                                <OpenReorderParametersButton parameters={model.Parameters} onChange={onParametersChange}/>
                                <AddTemplateParameterButton parameters={model.Parameters} onAdd={onAddParameter} type={ActionButtonType.Secondary}/>
                            </div>
                        </div>
                        <TemplateParametersTable parameters={model.Parameters} onChange={onParametersChange} platformHubAccounts={[]} processTemplateLink={undefined} nameFilter={nameFilter}/>
                    </PageContent>)}
            </LegacyForm>

            <ProjectTemplateCommitDialog open={isCommitPublishShareDialogOpen} gitRef={gitRef} onClose={closeCommitPublishShareDialog} onCommit={handleCommit} getCommandToCommit={getCommandToCommit} defaultCommitMessage="Update parameter set" entityName="parameter set"/>
        </>);
}
export async function getParameterSet(repository: Repository, projectTemplateSlug: string, gitRef: string) {
    try {
        const parameterSet = await repository.ProjectTemplates.getParameterSet({
            ProjectTemplateSlug: projectTemplateSlug,
            GitRef: gitRef,
        });
        return parameterSet;
    }
    catch (error) {
        if (error.ErrorMessage && error.ErrorMessage.match(/The file '\.octopus\/.*?\/parameters\.ocl' on the '.*?' branch does not exist in the Git repository\./)) {
            logger.error("Failed to get parameter set", error);
            return {
                Id: projectTemplateSlug,
                TemplateSlug: projectTemplateSlug,
                Parameters: [],
            };
        }
        throw error;
    }
}
const styles = {
    headerContainer: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingLeft: space[16],
        paddingRight: space[16],
        paddingTop: space[24],
        paddingBottom: space[24],
        borderTop: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
    }),
    parameterButtonsContainer: css({
        alignItems: "center",
        display: "flex",
        gap: space[12],
    }),
    commitButtonContainerStyles: css({
        display: "inline-flex",
        gap: space[4],
    }),
};
